import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import styled from '@emotion/styled';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import { LearningPathStore, LearningPathWorksheetStore } from '@seedlang/stores';
import { Exercise, WorksheetTag } from '@seedlang/models';
import Spinner from 'components/spinner';
import autobind from 'autobind-decorator';
import { gaps } from 'components/layout/stack';
import BannerNoticeIndex from 'components/banner_notice/banner_notice_index';
import LearningPathDropdown from 'components/learning_path/learning_path_dropdown';
import { AppUI, ExerciseUI } from '@seedlang/state';
import WorksheetPreview from 'components/worksheet/worksheet_preview';
import WelcomeScreen from 'components/learning_path/welcome_screen';
import ScrollTarget from 'components/learning_path/scroll_target';
import DesktopPath from 'components/learning_path/desktop_path';
import MobilePath from 'components/learning_path/mobile_path';

const Wrapper = styled.div`
  margin: 0 auto -10px auto !important;
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  max-width: 880px;
  width: 100%;

  @media (max-width: 1000px) {
    padding: 20px 20px 0px;
  }
`;

const WorksheetPreviewWrapper = styled.div`
  padding-bottom: ${gaps.md};
`;

const WelcomeScreenWrapper = styled.div`
  padding: ${gaps['2xl']} 0;

  @media (max-width: 960px) {
    padding: ${gaps.md};
  }
`;

const PreviewAndPathGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  align-items: start;
  gap: ${gaps.lg};
  margin: auto;

  @media (max-width: 699px) {
    grid-template-columns: 1fr;
  }
`;

const SpinnerWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

@observer
class LearningPath extends React.Component {
  @observable selectedWorksheetId = null;
  @observable showWelcomeScreen = false;

  componentDidMount() {
    LearningPathStore.getIndex({
      filters: { published: true },
      queryStrings: { include_progress: !!this.selectedLearningPathId },
    }, response => {
      LearningPathStore.setIndexData(response);

      const pathId = this.selectedLearningPathId;
      if (!pathId || !response.some(item => item.id === pathId)) return;

      this.loadSelectedWorksheetFromStorage(pathId);

      // adding ?welcome forces to show the welcome screen
      if (new URLSearchParams(window.location.search).has('welcome')) {
        this.showWelcomeScreen = true;
        return;
      }

      this.loadLearningPath(pathId);
    });
  }

  componentWillUnmount() {
    AppUI.worksheetStore.clearShowData();
  }

  @computed get selectedLearningPathId() {
    return AppUI.user.currentUserGroup?.learningPathId;
  }

  @autobind loadLearningPath(learningPathId) {
    LearningPathStore.getShow({
      ids: { learningPathId },
    }, response => {
      LearningPathStore.setShowData(response);
      if (this.selectedWorksheetId) {
        this.loadSelectedWorksheet();
      } else {
        this.loadFirstWorksheet(learningPathId);
      }
    });
  }

  @autobind loadFirstWorksheet(learningPathId) {
    if (this.worksheets.length > 0) {
      const firstWorksheet = this.worksheets[0].worksheet;
      this.setSelectedWorksheetId(firstWorksheet.id, learningPathId);
      ExerciseUI.set('worksheetId', firstWorksheet.id);
      ExerciseUI.worksheetStore.setShowData(firstWorksheet);
      this.enrichWorksheet(firstWorksheet.id);
    }
  }

  @autobind loadSelectedWorksheet() {
    const worksheet = this.worksheets.find(item => item.worksheet?.id === this.selectedWorksheetId)?.worksheet;
    if (worksheet) {
      ExerciseUI.set('worksheetId', worksheet.id);
      ExerciseUI.worksheetStore.setShowData(worksheet);
      this.enrichWorksheet(worksheet.id);
      this.scrollToSelectedWorksheet();
    } else {
      this.loadFirstWorksheet(this.selectedLearningPathId);
    }
  }

  @autobind loadSelectedWorksheetFromStorage(learningPathId) {
    const storageKey = `learningPath_${learningPathId}_selectedWorksheet`;
    const storedId = localStorage.getItem(storageKey);
    if (storedId) {
      this.selectedWorksheetId = storedId;
    }
  }

  @autobind setSelectedWorksheetId(worksheetId, learningPathId) {
    this.selectedWorksheetId = worksheetId;
    const storageKey = `learningPath_${learningPathId}_selectedWorksheet`;
    localStorage.setItem(storageKey, worksheetId);
  }

  scrollToSelectedWorksheet() {
    setTimeout(() => {
      const element = document.getElementById(this.selectedWorksheetId);
      if (!element) return;
      const container = document.getElementById('scrollTarget');
      container.scrollTop = element.offsetTop - container.offsetTop;
    }, 100);
  }

  @computed get worksheets() {
    return LearningPathStore.showData?.learningPathWorksheets || [];
  }

  @autobind handleLearningPathChange(learningPathId) {
    this.showWelcomeScreen = false;
    LearningPathStore.clearShowData();
    this.selectedWorksheetId = null;
    this.loadSelectedWorksheetFromStorage(learningPathId);
    this.loadLearningPath(learningPathId);
    this.updateUserGroupLearningPath(learningPathId);
  }

  @autobind updateUserGroupLearningPath(learningPathId) {
    if (AppUI.user.currentUserGroup) {
      AppUI.user.currentUserGroup.learningPathId = learningPathId;
      AppUI.userGroupStore.update({
        ids: { userGroupId: AppUI.user.currentUserGroup.id },
        data: { learningPathId },
      });
    }
  }

  @autobind enrichWorksheet(worksheetId) {
    LearningPathWorksheetStore.enrichWorksheet({
      ids: { worksheetId },
    }, enrichedData => {
      const currentWorksheet = ExerciseUI.worksheetStore.showData;
      if (currentWorksheet && currentWorksheet.id !== worksheetId) {
        return;
      }
      if (enrichedData.exercises) {
        const exerciseInstances = enrichedData.exercises.map(exerciseData => new Exercise(exerciseData));
        currentWorksheet.exercises = exerciseInstances;
      }
      if (enrichedData.worksheetTags) {
        const worksheetTagInstances = enrichedData.worksheetTags.map(worksheetTagData => new WorksheetTag(worksheetTagData));
        currentWorksheet.worksheetTags = worksheetTagInstances;
      }
    });
  }

  @autobind handleWorksheetClick(worksheetId) {
    const worksheet = this.worksheets.find(item => item.worksheet?.id === worksheetId)?.worksheet;
    if (worksheet) {
      this.setSelectedWorksheetId(worksheetId, this.selectedLearningPathId);
      ExerciseUI.set('worksheetId', worksheetId);
      ExerciseUI.worksheetStore.setShowData(worksheet);
      this.enrichWorksheet(worksheetId);
    }
  }

  render() {
    if (!LearningPathStore.hasIndexData) {
      return (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      );
    }

    if (!this.selectedLearningPathId || this.showWelcomeScreen) {
      return (
        <Wrapper>
          <WelcomeScreenWrapper>
            <WelcomeScreen
              learningPaths={LearningPathStore.indexData || []}
              signedIn={!!AppUI.userIsSignedInAndNotGuest}
              onLearningPathClick={this.handleLearningPathChange}
            />
          </WelcomeScreenWrapper>
        </Wrapper>
      );
    }

    if (!LearningPathStore.hasShowData) {
      return (
        <Wrapper>
          <BannerNoticeIndex route='learn.index' />
          <LearningPathDropdown
            value={this.selectedLearningPathId}
            onChange={this.handleLearningPathChange}
          />
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <BannerNoticeIndex route='learn.index' />
        <LearningPathDropdown
          value={this.selectedLearningPathId}
          onChange={this.handleLearningPathChange}
        />
        <PreviewAndPathGrid>
          {!AppUI.layout.isMobile &&
            <WorksheetPreviewWrapper id='worksheetPreviewWrapper'>
              <WorksheetPreview />
            </WorksheetPreviewWrapper>
          }
          <ScrollTarget secondaryTargetId='worksheetPreviewWrapper'>
            {AppUI.layout.isMobile ? (
              <MobilePath worksheets={this.worksheets} />
            ) : (
              <DesktopPath
                worksheets={this.worksheets}
                onWorksheetClick={this.handleWorksheetClick}
              />
            )}
          </ScrollTarget>
        </PreviewAndPathGrid>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(LearningPath);